import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useBackLayer } from "../backstack";
import { ShellIcon } from "./ShellIcons";

/**
 * A picture in the chat, big (prototype v2.20 chat.js ChatViewer): the page goes dark and soft behind it, who sent it
 * and when under it, ✕ at its corner. A press outside, Esc or the phone's back closes it; ← → (and the round arrows
 * beside it) step through the chat's other pictures. Before, a picture was a link that opened a new tab and took the
 * agent out of the console (QA G2-03). Its own, not 素材库's Viewer: that one has a column of words beside the file.
 */
export default function ChatViewer({
  pic,
  caption,
  by,
  pos,
  onPrev,
  onNext,
  onClose,
}: {
  /** The picture itself (Transcript draws it from the chat's own copy). */
  pic: ReactNode;
  caption: string;
  /** 「Madelyne · 14:32」 */
  by: string;
  /** 「2 / 5」, with more than one picture in the chat. */
  pos?: string;
  onPrev?: () => void;
  onNext?: () => void;
  onClose: () => void;
}) {
  const x = useRef<HTMLButtonElement | null>(null);
  // A phone's back closes the picture, not the chat under it.
  useBackLayer(true, onClose);
  // The keys go to the viewer while it is up, and back to the picture pressed when it goes.
  useEffect(() => {
    const was = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    x.current?.focus({ preventScroll: true });
    // A clip in the thread is drawn on a layer of its own that the blur behind never reaches: it showed through sharp.
    document.documentElement.classList.add("wc-viewing");
    return () => {
      document.documentElement.classList.remove("wc-viewing");
      was?.focus({ preventScroll: true });
    };
  }, []);
  useEffect(() => {
    // Caught on the way down, so the Esc that closes the picture closes nothing under it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      } else if (e.key === "ArrowLeft" && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === "ArrowRight" && onNext) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onPrev, onNext, onClose]);

  return createPortal(
    <div className="wc-view" role="dialog" aria-modal="true" aria-label="看图片" onClick={onClose}>
      <figure className="wc-view-in" onClick={(e) => e.stopPropagation()}>
        {pic}
        <figcaption>
          <span className="wc-view-cap">{caption}</span>
          {pos && <span className="wc-view-pos">{pos}</span>}
          <span className="wc-view-by">{by}</span>
        </figcaption>
        <button ref={x} type="button" className="wc-view-x" aria-label="关掉" title="关掉" onClick={onClose}>
          <ShellIcon name="x" />
        </button>
        {onPrev && (
          <button type="button" className="wc-view-nav prev" aria-label="上一张" title="上一张" onClick={onPrev}>
            <ShellIcon name="left" size={22} />
          </button>
        )}
        {onNext && (
          <button type="button" className="wc-view-nav next" aria-label="下一张" title="下一张" onClick={onNext}>
            <ShellIcon name="right" size={22} />
          </button>
        )}
      </figure>
    </div>,
    document.body,
  );
}
